import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { Image, Link, Typography } from '@/components/atoms';
import {
  PlayerAddToButton,
  PlayerNextButton,
  PlayerPlayToggleButton,
  PlayerPreviousButton,
  PlayerScrubber,
  PlayerSettingsMenuButtonAudio,
} from '@/components/atoms/PlayerControl';
import { LikeButton } from '@/components/molecules';
import { FavouriteType, useFavourite } from '@/lib/contexts/favouriting';
import { Duration } from '@/lib/utils/time-formatting';

import type { TrackFileType } from '@/lib/api';
import type {
  CompilationNormalized,
  TrackNormalized,
} from '@/lib/contexts/media-controller/util';

interface Props {
  compilation?: CompilationNormalized;
  track?: TrackNormalized;
  duration?: Duration;
  availableFormats: TrackFileType[];
  preferredFormat?: TrackFileType;
  image?: string;
  disablePrevious?: boolean;
  disableNext?: boolean;
  onPauseExplicit?: () => void;
  onFormatSelect?: (format: TrackFileType) => void;
  onPrevious?: () => void;
  onNext?: () => void;
}

export function ExpandedPlayer({
  compilation,
  track,
  duration,
  availableFormats,
  preferredFormat,
  image,
  disablePrevious = false,
  disableNext = false,
  onPauseExplicit = () => {},
  onFormatSelect = () => {},
  onPrevious = () => {},
  onNext = () => {},
}: Props) {
  const t = useTranslations();
  const { isFavourite, favourites } = useFavourite();

  const isLiked = useMemo(() => {
    const id = track?.trackId ? Number(track.trackId) : null;
    if (!id || Number.isNaN(id)) return false;
    return isFavourite(FavouriteType.Track, id);
    // depends on favourites so UI updates immediately when toggled
  }, [track?.trackId, favourites, isFavourite]);

  const renderCredits = () => {
    return track?.credits.map((credit, index) => (
      <div key={`credit-${index}`} className="flex flex-col py-2.5 gap-1">
        <Typography.Text
          className="text-(--player-text-color-secondary)"
          size="sm"
          weight="semibold"
        >
          {credit.label}
        </Typography.Text>
        <Typography.Text size="sm">{credit.value}</Typography.Text>
      </div>
    ));
  };

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-4">
        {compilation && (
          <div className="relative aspect-square">
            <Image
              className="rounded-lg size-full cover object-cover"
              src={image ?? compilation.images.xlarge}
              alt="Album image"
              fill
            />
          </div>
        )}
        <div className="hidden">
          <div>
            <Typography.Text
              className="inline mr-1 text-(--player-text-color-secondary)"
              variant="paragraph"
            >
              {t('EXPANDED_PLAYER_PLAYING_FROM')}
            </Typography.Text>
            <Link href={`playlist/${compilation?.id}`}>
              <Typography.Text className="inline">
                {compilation?.title}
              </Typography.Text>
            </Link>
          </div>
        </div>

        <div>
          <div>
            <Typography.Text
              className="text-(--player-text-color-secondary)"
              size="sm"
            >
              {track?.trackComposer}
            </Typography.Text>
            <Typography.Text
              className="text-(--player-text-color-primary)"
              size="md"
              weight="semibold"
            >
              {track?.trackTitle}
            </Typography.Text>
            <Typography.Text
              className="text-(--player-text-color-secondary)"
              size="sm"
            >
              {track?.trackPerformers}
            </Typography.Text>
          </div>
        </div>

        <div className="flex">
          {duration && (
            <PlayerScrubber duration={duration} mediaController="mini_player" />
          )}
        </div>

        <div className="grid grid-cols-3">
          <div className="flex justify-start">
            <PlayerPreviousButton
              size="3xl"
              disabled={disablePrevious}
              onClick={onPrevious}
            />
          </div>
          <div className="flex justify-around items-center">
            <PlayerPlayToggleButton
              size="2xl"
              mediaController="mini_player"
              onPause={onPauseExplicit}
            />
          </div>
          <div className="flex justify-end">
            <PlayerNextButton
              size="2xl"
              disabled={disableNext}
              onClick={onNext}
            />
          </div>
        </div>

        <div className="grid grid-cols-3">
          <div className="flex justify-start">
            <div className="flex flex-col items-center">
              {track?.trackId && (
                <LikeButton
                  contentId={Number(track.trackId)}
                  contentType="Track"
                  disabled={compilation?.type === 'podcast'}
                  iconOnly
                />
              )}
              <Typography.Text size="sm">
                {isLiked
                  ? t('EXPANDED_PLAYER_LIKED_TRACK')
                  : t('EXPANDED_PLAYER_LIKE_TRACK')}
              </Typography.Text>
            </div>
          </div>

          <div className="flex justify-around">
            <div className="flex flex-col items-center">
              <PlayerAddToButton contentId={Number(track?.trackId)} />
              <Typography.Text size="sm">
                {t('EXPANDED_PLAYER_ADD_TO')}
              </Typography.Text>
            </div>
          </div>

          <div className="flex justify-end relative">
            {availableFormats.length > 1 && (
              <div className="flex flex-col items-center">
                <PlayerSettingsMenuButtonAudio
                  availableFormats={availableFormats}
                  preferredRendition={preferredFormat}
                  handleOnRenditionSelect={onFormatSelect}
                />
                <Typography.Text size="sm">
                  {t('EXPANDED_PLAYER_SETTINGS')}
                </Typography.Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {track && track.credits.length > 0 && (
        <div className="flex flex-col gap-2">
          <Typography.Text size="sm" weight="semibold">
            {t('EXPANDED_PLAYER_CREDITS')}
          </Typography.Text>
          <div className="divide-y divide-(--color-border-neutral-soft)">
            {renderCredits()}
          </div>
        </div>
      )}
    </div>
  );
}
